import React from 'react';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { useNavigate } from 'react-router';
import { ROUTES } from '@constants/routes';
import { Container, H5, Page } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { createTimeInstance } from '@utils/time';
import { SCREEN_SIZES } from '@constants/screen';
import { TrackingEvent } from '@classes/TrackingEvent';
import { scrollToTop } from '@utils/usefulFuncs';
import { MultiColumnPage } from '@ui/MultiColumnPage';
import { useGetRecentDocs } from '@hooks/features/useRecentDocsAndNews';
import { DailyDocsList } from '@containers/DigitalAnalyst/DailyDocsList';
import { FilterForm } from '@containers/DigitalAnalyst/components/DocFilters';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { DailySummary } from '@containers/DigitalAnalyst/DailySummary';
import { AI_TOOLS } from '@constants';
import { useScreenSize } from '@hooks/application/useWindowSize';
import { ColumnTabs } from '@containers/UI/ColumnTabs';
import { useWatchlist } from '@hooks/features/useWatchlist';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { useSelector } from 'react-redux';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SideBySidePages = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 16px;
  margin: 0 16px;
`;

const tool = AI_TOOLS.LATEST_UPDATES_AND_NEWS;

const tickerFilterKey = 'tfk';
const getTickerFiltersFromStorage = () => {
  const v = window.localStorage.getItem(tickerFilterKey);
  return v === null || typeof v !== 'string' ? '' : v;
};
const setTickerFiltersFromStorage = (v) => {
  window.localStorage.setItem(tickerFilterKey, v);
};

const defaultTickers = getTickerFiltersFromStorage();

const defaultMinMarketCap = 5000000000;
const defaultMaxMarketCap = 10000000000000;
const defaultFilterDocTypes = [];
// Doc types excluded from the default filter selection (users can re-enable them
// via the filter form). Form 4 filings are noisy, so drop them by default.
const DEFAULT_EXCLUDED_DOC_TYPES = ['4'];
const buildDefaultDocTypes = (allDocTypes) =>
  Array.isArray(allDocTypes) && allDocTypes.length > 0
    ? allDocTypes.filter((t) => !DEFAULT_EXCLUDED_DOC_TYPES.includes(t))
    : defaultFilterDocTypes;
const defaultStartDate = createTimeInstance().subtract(7, 'days');
const defaultOnlyWatchlist = defaultTickers.length > 0 ? false : false;
const noWatchlistTickers = null;

const filterSettingsFilterKey = 'fskey';
const getSavedFiltersFromStorage = () => {
  const v = window.localStorage.getItem(filterSettingsFilterKey);
  if (v === null || v === 'null' || typeof v !== 'string') {
    return {
      minMarketCap: defaultMinMarketCap,
      maxMarketCap: defaultMaxMarketCap,
      docTypes: defaultFilterDocTypes,
      startDate: defaultStartDate,
      ignoreTickers: false,
      onlyWatchlist: defaultOnlyWatchlist,
    };
  }
  return JSON.parse(v);
};
const hasSavedFilters = () => {
  const v = window.localStorage.getItem(filterSettingsFilterKey);
  return !(v === null || v === 'null' || typeof v !== 'string');
};
const setSavedFiltersToStorage = (v) => {
  window.localStorage.setItem(filterSettingsFilterKey, JSON.stringify(v));
};

const TAB_NAMES = {
  docs: 'Documents',
  summary: 'Summary',
};

export function LatestUpdatesAndNews() {
  const navigate = useNavigate();
  const screenSize = useScreenSize();

  const permissions = useCurrentUserToolsAvailable();
  const isWatchlistPermissionEnabled = permissions.includes(AI_TOOLS.WATCHLIST);

  const [DailySummaryComponent] = React.useState(<DailySummary key={'daily-summary'} />);
  const [DailyDocsListComponent] = React.useState(<DailyDocsList key={'daily-docs-list'} />);

  const { watchlist } = useWatchlist();

  const allDocTypes = useSelector((state) => state.constants.docTypes);

  const { enableDailySummaries: showDailySummary } = useAdvancedSettings();
  const {
    minMarketCap: savedMinMarketCap,
    maxMarketCap: savedMaxMarketCap,
    docTypes: savedDocTypes,
    startDate: savedStartDate,
    ignoreTickers: savedIgnoreTickers,
    onlyWatchlist: savedOnlyWatchlist,
  } = getSavedFiltersFromStorage();
  const [minMarketCap, setMinMarketCap] = React.useState(savedMinMarketCap);
  const [maxMarketCap, setMaxMarketCap] = React.useState(savedMaxMarketCap);
  const [startDate, setStartDate] = React.useState(savedStartDate);
  const [filterDocTypes, setFilterDocTypes] = React.useState(() =>
    hasSavedFilters() ? savedDocTypes : buildDefaultDocTypes(allDocTypes)
  );

  // Apply the default Form 4 exclusion once the doc-type universe loads, for
  // users who have not saved custom filters (the list may be empty at mount).
  const skipDefaultDocTypes = React.useRef(hasSavedFilters() || buildDefaultDocTypes(allDocTypes).length > 0);
  React.useEffect(() => {
    if (skipDefaultDocTypes.current) return;
    const nextDocTypes = buildDefaultDocTypes(allDocTypes);
    if (nextDocTypes.length === 0) return;
    skipDefaultDocTypes.current = true;
    setFilterDocTypes(nextDocTypes);
  }, [allDocTypes]);
  const [tickers, setTickers] = React.useState(defaultTickers);
  const [ignoreTickers, setIgnoreTickers] = React.useState(savedIgnoreTickers);
  const [onlyWatchlist, setOnlyWatchlist] = React.useState(savedOnlyWatchlist);

  useGetRecentDocs(
    minMarketCap,
    maxMarketCap,
    startDate,
    filterDocTypes,
    tickers,
    ignoreTickers,
    onlyWatchlist && isWatchlistPermissionEnabled ? watchlist : noWatchlistTickers
  );

  const onGoBack = () => navigate(ROUTES.home);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  const mobileTabNames = React.useMemo(() => {
    const tabs = [TAB_NAMES.docs];
    showDailySummary && tabs.push(TAB_NAMES.summary);
    return tabs;
  }, [showDailySummary]);

  const mobileBodyComponents = React.useMemo(() => {
    const components = [DailyDocsListComponent];
    showDailySummary && components.push(DailySummaryComponent);
    return components;
  }, [showDailySummary, DailyDocsListComponent, DailySummaryComponent]);

  const resetFilters = () => {
    setMinMarketCap(defaultMinMarketCap);
    setMaxMarketCap(defaultMaxMarketCap);
    setFilterDocTypes(buildDefaultDocTypes(allDocTypes));
    setStartDate(defaultStartDate);
    setOnlyWatchlist(defaultOnlyWatchlist);
    setTickers('');
    setIgnoreTickers(false);
    setTickerFiltersFromStorage('');
    setSavedFiltersToStorage(null);

    TrackingEvent.create('Reset Recent Docs Filters', {});
  };

  const onFiltersChange = (values) => {
    const { minMarketCap, maxMarketCap, docTypes, startDate, tickers, ignoreTickers, onlyWatchlist } = values;
    setMinMarketCap(minMarketCap);
    setMaxMarketCap(maxMarketCap);
    setFilterDocTypes(docTypes);
    setStartDate(startDate);
    setTickers(tickers);
    setOnlyWatchlist(onlyWatchlist);
    setIgnoreTickers(ignoreTickers);

    setTickerFiltersFromStorage(tickers);
    setSavedFiltersToStorage({ minMarketCap, maxMarketCap, docTypes, startDate, ignoreTickers, onlyWatchlist });

    TrackingEvent.create('Update Recent Docs Filters', {
      'Market Cap Min': minMarketCap,
      'Market Cap Max': maxMarketCap,
      'Doc Types': docTypes,
      'Start Date': startDate,
      Tickers: tickers,
      'Only Watchlist': onlyWatchlist,
      'Toggle Saved Tickers On': ignoreTickers,
    });
  };

  if (screenSize === SCREEN_SIZES.mobile) {
    return (
      <PageWrapper>
        <Header />
        <Body withFooter>
          <Page>
            <BackArrowButton onClick={onGoBack} />
            <AdvancedSettingsButton top={8} tool={tool} />

            <Container top={36}>
              <H5>Latest Updates</H5>
            </Container>
            <Container>
              <FilterForm
                minMarketCap={minMarketCap}
                maxMarketCap={maxMarketCap}
                docTypes={filterDocTypes}
                startDate={startDate}
                tickers={tickers}
                ignoreTickers={ignoreTickers}
                onFiltersChange={onFiltersChange}
                onResetFilters={resetFilters}
                onlyWatchlist={onlyWatchlist}
                enableWatchlist={isWatchlistPermissionEnabled}
                enableMarketCap
                enableTickers
                enableWatchlistLink
              />
            </Container>
            <Container top={24}>
              {showDailySummary ? (
                <ColumnTabs tabNames={mobileTabNames} BodyComponents={mobileBodyComponents} />
              ) : (
                DailyDocsListComponent
              )}
            </Container>
          </Page>
        </Body>
      </PageWrapper>
    );
  }

  if (screenSize === SCREEN_SIZES.tablet) {
    return (
      <PageWrapper>
        <Header />
        <Body withFooter>
          <SideBySidePages>
            <MultiColumnPage>
              <BackArrowButton onClick={onGoBack} />
              <AdvancedSettingsButton top={8} tool={tool} />

              <Container top={36}>
                <H5>Sources</H5>
              </Container>
              <Container>
                <FilterForm
                  minMarketCap={minMarketCap}
                  maxMarketCap={maxMarketCap}
                  docTypes={filterDocTypes}
                  startDate={startDate}
                  tickers={tickers}
                  ignoreTickers={ignoreTickers}
                  onFiltersChange={onFiltersChange}
                  onResetFilters={resetFilters}
                  onlyWatchlist={onlyWatchlist}
                  enableWatchlist={isWatchlistPermissionEnabled}
                  enableMarketCap
                  enableTickers
                  enableWatchlistLink
                />
              </Container>
              <Container top={24}>
                <DailyDocsList />
              </Container>
            </MultiColumnPage>
            {showDailySummary && <MultiColumnPage>{DailySummaryComponent}</MultiColumnPage>}
          </SideBySidePages>
        </Body>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <SideBySidePages>
          <MultiColumnPage>
            <BackArrowButton onClick={onGoBack} />
            <AdvancedSettingsButton top={8} tool={tool} />

            <Container top={36}>
              <H5>Sources</H5>
            </Container>
            <Container>
              <FilterForm
                minMarketCap={minMarketCap}
                maxMarketCap={maxMarketCap}
                docTypes={filterDocTypes}
                startDate={startDate}
                tickers={tickers}
                ignoreTickers={ignoreTickers}
                onFiltersChange={onFiltersChange}
                onResetFilters={resetFilters}
                onlyWatchlist={onlyWatchlist}
                enableWatchlist={isWatchlistPermissionEnabled}
                enableMarketCap
                enableTickers
                enableWatchlistLink
              />
            </Container>
            <Container top={24}>{DailyDocsListComponent}</Container>
          </MultiColumnPage>
          {showDailySummary && <MultiColumnPage>{DailySummaryComponent}</MultiColumnPage>}
        </SideBySidePages>
      </Body>
    </PageWrapper>
  );
}
